import { Text } from '@loomhq/lens';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import {
  endThumbnailFlow,
  initiateThumbnailSave,
  setLocalThumbnail,
} from 'actions/editor';
import { showErrorBar } from 'actions/errorBar';
import { ERROR } from 'constants/error-severities';
import { GIF_SETTINGS_UPDATE } from 'constants/events';
import { removeVideoThumbnail, updateGifSettings } from 'creators/shareVideo';
import * as analytics from 'utilities/analytics';
import { getCloudfrontURI } from 'utilities/avatar';

import TrashImg from 'img/icons/new/trash.svg';
import DragAndDropImg from 'img/illustrations/drag-and-drop.svg';

import { LOOM_PRO as LOOM_PRO_CAPABILITY } from '../../../../../shared/constants/capabilities';
import {
  OG_THUMB_FULL,
  THUMB_FULL,
} from '../../../../../shared/constants/video';

import SharePageDropzone from 'components/share-video/share-page-dropzone';
import Button from 'components/ui-components/button';
import Checkbox from 'components/ui-components/checkbox';
import ToggleSwitch from 'components/ui-components/toggle-switch';

import './styles.less';

class ThumbnailWidget extends React.Component {
  state = {
    useGifDefault: null,
    useGif: null,
    interactedWith: false,
  };

  render() {
    const {
      localThumbnail,
      uploading,
      thumbnails,
      preferredDims,
      supportGifs,
    } = this.props;
    const { interactedWith, useGifDefault, useGif } = this.state;

    const hasThumbnail = (thumbnails || {})[THUMB_FULL];
    const isGifSettings = !localThumbnail && interactedWith;

    return (
      <div className="thumbnail-form">
        {/* make sure this is rendered first so the rest of the widget lies above it */}
        <SharePageDropzone multiple={false} onDrop={this.handleDropzoneDrop} />
        <div className="edit-thumbnail-header">
          <Text variant="title">
            {hasThumbnail ? 'Edit thumbnail' : 'Upload a thumbnail'}
          </Text>
        </div>
        <img src={DragAndDropImg} className="thumb-drag-and-drop-illo" />
        {!hasThumbnail && (
          <Fragment>
            <div className="thumb-instructions">Drag and drop to upload</div>
            <div className="thumb-fine-print">
              {preferredDims.width} x {preferredDims.height}px .pngs, and .jpgs
              work best!
            </div>
          </Fragment>
        )}
        {// spacer
        localThumbnail && <div>&nbsp;</div>}
        {hasThumbnail && (
          <Fragment>
            <div className="thumb-instructions">
              Adjust positioning and zoom
            </div>
            <div className="thumb-fine-print">
              delete to use a different thumbnail
            </div>
          </Fragment>
        )}
        {hasThumbnail && (
          <div
            className="thumb-delete-section"
            onClick={this.handleDeleteThumbnail}
          >
            <img src={TrashImg} className="delete-trash-icon" />
            <div>Delete thumbnail</div>
          </div>
        )}
        {supportGifs && !hasThumbnail && !localThumbnail && (
          <div className="animated-preview">
            <div className="animated-preview-switch">
              <span onClick={this.toggleProp('useGif')}>
                Animated Thumbnail (gif)
              </span>
              <ToggleSwitch
                index="use-gif"
                status={useGif || false}
                onClick={this.toggleProp('useGif')}
              />
            </div>
            <div className="animated-preview-default">
              <Checkbox
                isChecked={useGifDefault || false}
                onEnable={this.toggleProp('useGifDefault')}
                onDisable={this.toggleProp('useGifDefault')}
              />
              <span onClick={this.toggleProp('useGifDefault')}>
                Use this setting for all videos
              </span>
            </div>
          </div>
        )}
        <div className="button-actions">
          <Button
            buttonClass="action-button"
            buttonType="rp-secondary-3"
            value="Cancel"
            disabled={uploading}
            onClicked={this.handleExitFlow}
          />
          {!isGifSettings && (
            <Button
              buttonClass="action-button"
              buttonType="rp-primary-3"
              disabled={uploading}
              value={localThumbnail ? 'Save' : 'Browse'}
              onClicked={this.handleAddCoverClick}
            />
          )}
          <input
            type="file"
            id="upload-thumbnail-file"
            onClick={this.handleUploadCoverClick}
            onChange={this.handleBrowseInput}
            accept="image/jpeg,image/pjpeg,image/png"
          />
          {isGifSettings && (
            <Button
              buttonClass="action-button"
              buttonType="rp-primary-3"
              value={'Save'}
              disabled={uploading}
              onClicked={this.handleSaveGif}
            />
          )}
        </div>
      </div>
    );
  }

  toggleProp = prop => () => {
    this.setState({ [prop]: !this.state[prop], interactedWith: true });
  };

  handleDeleteThumbnail = () => {
    const { videoId } = this.props;

    this.props.removeVideoThumbnail(videoId);
    this.props.setLocalThumbnail(null);
  };

  handleExitFlow = () => {
    this.props.setLocalThumbnail(null);
    this.props.endThumbnailFlow();
  };

  handleDropzoneDrop = files => {
    const file = files[0];

    if (!file) {
      return;
    }

    this.handleFile(file);
  };

  handleBrowseInput = event => {
    const {
      target: { files },
    } = event;
    const file = files[0];

    this.handleFile(file);

    event.target.value = null;
  };

  readFile = blob => {
    const reader = new FileReader();

    // Closure to capture the file information.
    reader.onload = e => {
      const imgSrc = e.target.result;
      const img = new Image();

      img.onload = () => {
        this.props.setLocalThumbnail(imgSrc, {
          width: img.width,
          height: img.height,
        });
      };
      img.src = imgSrc;
    };

    // Read in the image file as a data URL.
    reader.readAsDataURL(blob);
  };

  handleFile = file => {
    if (!file) {
      return;
    }

    this.readFile(file);
  };

  handleUploadCoverClick = event => {
    event.stopPropagation();
  };

  handleAddCoverClick = () => {
    if (this.props.localThumbnail) {
      this.props.initiateThumbnailSave();

      return;
    }

    document.querySelector('#upload-thumbnail-file').click();
  };

  handleSaveGif = () => {
    const { useGif, useGifDefault } = this.state;
    const { videoId } = this.props;

    analytics.track(GIF_SETTINGS_UPDATE, {
      useGif,
      useGifDefault,
      videoId,
    });

    this.props.updateGifSettings(videoId, useGif, useGifDefault && useGif);
  };

  componentDidMount() {
    const { thumbnails } = this.props;
    const ogThumbnail = (thumbnails || {})[OG_THUMB_FULL];
    const { useGif = false } = this.props;

    this.setState({ useGif });

    if (!ogThumbnail) {
      return;
    }

    fetch(getCloudfrontURI(ogThumbnail))
      .then(resp => {
        if (!resp.ok) {
          throw new Error('Failed to fetch current thumbnail');
        }

        return resp.blob();
      })
      .then(thumbBlob => {
        this.readFile(thumbBlob);
      })
      .catch(() => {
        this.props.showErrorBar(
          ERROR,
          'Oops! failed to fetch current thumbnail, please delete current thumbnail to upload new image'
        );
      });
  }
}

const mapStateToProps = state => {
  const {
    currentUserIsOwner,
    video,
    download_url: downloadUrl,
  } = state.share_video;
  const { width, height } = state.video_player;
  const { thumbnail_data } = state.editor;
  const { capabilities } = state.user;

  return {
    currentUserIsOwner,
    videoId: video.id,
    thumbnails: video.thumbnails,
    localThumbnail: thumbnail_data.thumbnail_local,
    uploading: thumbnail_data.uploading,
    preferredDims: { width, height },
    useGif: video.use_gif,
    supportGifs:
      capabilities && capabilities.includes(LOOM_PRO_CAPABILITY) && downloadUrl,
  };
};

const mapDispatchToProps = dispatch => ({
  removeVideoThumbnail: videoId => dispatch(removeVideoThumbnail(videoId)),
  endThumbnailFlow: () => dispatch(endThumbnailFlow()),
  setLocalThumbnail: (url, dims) => dispatch(setLocalThumbnail(url, dims)),
  initiateThumbnailSave: () => dispatch(initiateThumbnailSave()),
  showErrorBar: (severity, message) =>
    dispatch(showErrorBar(severity, message)),
  showMessage: msg => dispatch(showConfirmationModal(msg)),
  updateGifSettings: (videoId, useGif, useGifDefault) =>
    dispatch(updateGifSettings(videoId, useGif, useGifDefault)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThumbnailWidget);
